import { proformApiQuery } from '@eliadmin/core/src/api/proform/proform-api';
import { type ProformInvoice } from '@eliadmin/core/src/api/proform/proform-types';
import {
  ProfileInvoiceDialog,
  getDruhDokladu,
} from '@eliadmin/core/src/components/profile/profile-invoice-dialog';
import { ProfileInvoiceStatus } from '@eliadmin/core/src/components/profile/profile-invoice-status';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Card, CardContent } from '@eliadmin/core/src/components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@eliadmin/core/src/components/ui/tooltip';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { formatPrice, roundPrice } from '@eliadmin/core/src/utils/price-utils';
import { downloadBase64File } from '@eliadmin/core/src/utils/proform-utils';
import { useQuery } from '@tanstack/react-query';
import { FiSearch } from 'react-icons/fi';
import { LuDownload } from 'react-icons/lu';

export type ProfileInvoiceItemProps = {
  invoice: ProformInvoice;
};

export function ProfileInvoiceItem(props: ProfileInvoiceItemProps) {
  const currencySettings = useGlobalStore(s => s.generalLayout.currency_settings);
  const currency = useGlobalStore(s => s.generalLayout.currency);
  const customer = useCustomerStore(s => s.me!.customer);
  const { t } = useTranslations();

  const invoiceDoklad = useQuery({
    queryKey: ['ELI_GET_INVOICE', props.invoice.doklad],
    queryFn: () =>
      proformApiQuery('ELI_GET_INVOICE', {
        e_mail: customer.email,
        doklad: props.invoice.doklad,
      }),
    enabled: false,
  });

  return (
    <Card className="rounded-none border-b first:border-t @xl:rounded-xl @xl:border">
      <CardContent className="hidden flex-row items-center gap-4 p-4 @xl:flex">
        <span className="flex-1 text-start text-sm font-bold">{props.invoice.doklad}</span>
        <span className="flex-1 text-start text-sm">{props.invoice.var_symbol}</span>
        {/* <div className="flex flex-1 flex-row justify-start text-sm">
          <span>{getDruhDokladu(props.invoice.druh_dokladu)}</span>
        </div> */}
        <span className="flex-1 text-start text-sm" suppressHydrationWarning>
          {new Date(props.invoice.dat_vyst).toLocaleDateString()}
        </span>
        <span className="flex-1 text-start text-sm font-bold">
          {formatPrice(
            roundPrice(props.invoice.devizy),
            props.invoice.mena ?? currency,
            currencySettings,
          )}
        </span>
        <div className="flex flex-1 flex-row justify-start">
          <ProfileInvoiceStatus invoice={props.invoice} />
        </div>
        <div className="flex flex-1 flex-row justify-end gap-2">
          {String(props.invoice.attach) === '1' && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size={'icon-lg'}
                    variant="ghost"
                    isLoading={invoiceDoklad.isLoading}
                    className="text-primary"
                    onClick={async () => {
                      const result = await invoiceDoklad.refetch();
                      if (result.data && result.data.root.invoice) {
                        const { file_name, att } = result.data.root.invoice;
                        downloadBase64File(att, file_name);
                      }
                    }}
                  >
                    <LuDownload className="h-6 w-6" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t('text1227')}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          <TooltipProvider>
            <Tooltip>
              <ProfileInvoiceDialog invoice={props.invoice}>
                <TooltipTrigger asChild>
                  <Button size={'icon-lg'} variant="ghost" className="text-primary">
                    <FiSearch className="h-6 w-6" />
                  </Button>
                </TooltipTrigger>
              </ProfileInvoiceDialog>
              <TooltipContent>
                <p>{t('text338')}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </CardContent>
      <CardContent className="flex flex-col gap-4 p-4 @xl:hidden">
        <div className="flex flex-row items-center justify-between gap-2">
          <div className="flex flex-1 flex-col gap-1">
            <span className="flex-1 font-bold">{props.invoice.doklad}</span>
            <span className="flex-1 text-sm text-muted-foreground">{props.invoice.var_symbol}</span>
            <span className="flex-1 text-sm text-muted-foreground">{props.invoice.dat_vyst}</span>
          </div>
          <div className="flex flex-row justify-end gap-2">
            {String(props.invoice.attach) === '1' && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size={'icon-lg'}
                      variant="ghost"
                      className="text-primary"
                      isLoading={invoiceDoklad.isLoading}
                      onClick={async () => {
                        const result = await invoiceDoklad.refetch();
                        if (result.data && result.data.root.invoice) {
                          const { file_name, att } = result.data.root.invoice;
                          downloadBase64File(att, file_name);
                        }
                      }}
                    >
                      <LuDownload className="h-6 w-6" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t('text1227')}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            <TooltipProvider>
              <Tooltip>
                <ProfileInvoiceDialog invoice={props.invoice}>
                  <TooltipTrigger asChild>
                    <Button size={'icon-lg'} variant="ghost" className="text-primary">
                      <FiSearch className="h-6 w-6" />
                    </Button>
                  </TooltipTrigger>
                </ProfileInvoiceDialog>
                <TooltipContent>
                  <p>{t('text338')}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
        <div className="flex flex-row items-center justify-end gap-2"></div>
        <div className="flex flex-row items-center gap-2">
          <ProfileInvoiceStatus invoice={props.invoice} />
          <span className="flex-1 text-right font-bold">
            {formatPrice(
              roundPrice(props.invoice.devizy),
              props.invoice.mena ?? currency,
              currencySettings,
            )}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
